import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import IframeResizer from "iframe-resizer-react"
import { useAuth0 } from "@auth0/auth0-react"
import { navigate } from "@reach/router"
import { USE_LOCAL_DEVELOPMENT } from "../const"

type IframeProps = Readonly<{
  path: string
  searchParams?: Record<string, string>
}>

export const IFrame = ({ path, searchParams }: IframeProps) => {
  const iframeRef = useRef<HTMLIFrameElement>()
  const [token, setToken] = useState<string | null>(null)

  const isLocalhost = useMemo(() => window.location.href.includes(`localhost:8081`), [])

  const params = new URLSearchParams({
    embedIn: `portal`,
    origin: window.location.origin,
  })

  if (searchParams) {
    Object.entries(searchParams).forEach(([key, value]) => {
      params.append(key, value)
    })
  }
  const src = useMemo(() => {
    const basePath = isLocalhost
      ? `https://portal.${process.env.APP_ENV}.infra.flockcover.com/frontend/${path}`
      : `/frontend/${path}`
    return `${basePath}?${params.toString()}`
  }, [isLocalhost, path, searchParams])

  const { getAccessTokenSilently } = useAuth0()

  useEffect(() => {
    ;(async () => {
      try {
        setToken(await getAccessTokenSilently())
      } catch (error) {
        // @TODO - handle error case
      }
    })()
  }, [getAccessTokenSilently])

  useEffect(() => {
    const listener = (event: MessageEvent): void => {
      if (event.data === `get-token`) {
        // @ts-ignore - this works, regardless of the TS types.
        event.source.postMessage(token ? `token:${token}` : `unauth:`, event.origin)
      }

      if (event.data.type === `iframe-navigate`) {
        navigate(`/${event.data.url}`)
      }
    }

    window.addEventListener(`message`, listener, false)

    return () => {
      window.removeEventListener(`message`, listener, false)
    }
  }, [token])

  const sendScrollAndSize = useCallback(() => {
    const iframeWindow = iframeRef.current
    // @ts-ignore - this works, regardless of the TS types.
    if (!iframeWindow?.sendMessage) return

    const scrollElement = document.querySelector(`#OVERFLOWED_BASE_ELEMENT_ID`)
    const scrollTop = scrollElement?.scrollTop ?? 0
    const connectivityBar = document.querySelector<HTMLDivElement>(`div[data-testid="connectivity_target_bar"]`)
    const connectivityBarHeight = connectivityBar?.offsetHeight ?? 0

    // @ts-ignore - this works, regardless of the TS types.
    iframeWindow.sendMessage(
      {
        type: `PARENT_SCROLL_AND_SIZE`,
        scrollTop,
        clientWidth: window.innerWidth,
        clientHeight: window.innerHeight,
        connectivityBarHeight,
      },
      window.location.origin
    )
  }, [])

  useEffect(() => {
    const iframeElement = iframeRef.current
    if (iframeElement?.addEventListener) {
      iframeElement.addEventListener(`load`, sendScrollAndSize)
    }

    const scrollElement = document.querySelector(`#OVERFLOWED_BASE_ELEMENT_ID`)
    if (scrollElement) {
      scrollElement.addEventListener(`scroll`, sendScrollAndSize)
    }

    window.addEventListener(`resize`, sendScrollAndSize)

    return () => {
      iframeElement?.removeEventListener(`load`, sendScrollAndSize)
      scrollElement?.removeEventListener(`scroll`, sendScrollAndSize)
      window.removeEventListener(`resize`, sendScrollAndSize)
    }
  }, [sendScrollAndSize])

  return (
    <IframeResizer
      forwardRef={iframeRef}
      heightCalculationMethod="taggedElement"
      inPageLinks
      src={USE_LOCAL_DEVELOPMENT ? `http://localhost:3000/${path}?${params.toString()}` : src}
      style={{
        minWidth: `100%`,
        borderStyle: `unset`,
        height: `100%`,
        minHeight: `100vh`,
      }}
    />
  )
}
